/** SUNTRONIX ONE — Preflight Rule Editor (Suntronix internal).
 *
 *  Lists every registered Preflight rule.  Suntronix admins can:
 *    • Toggle a rule active / inactive (globally or per-customer)
 *    • Add customer-scoped policy overrides (e.g. Customer-A wants
 *      `mdm_required=false`, `buffer_stock_min=8`, `po_required=false`)
 *
 *  Full rule authoring is P1; this page ships the two most-requested
 *  admin actions today.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck, ShieldAlert, RefreshCcw, Sliders, ToggleLeft, ToggleRight, Plus,
} from "lucide-react";

export default function PreflightRules() {
  const { user } = useAuth();
  const isSuntronix = user && !user.master_company_id;
  const [rules, setRules] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [q, setQ] = useState("");
  const [entityKind, setEntityKind] = useState("");
  const [busy, setBusy] = useState({});
  const [addPolicy, setAddPolicy] = useState(false);
  const [pForm, setPForm] = useState({ key: "", value: "true",
    master_company_id: "", source_label: "" });

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        api.get("/preflight/rules", { params: entityKind ? { entity_kind: entityKind } : {} }),
        api.get("/preflight/customer-policies"),
      ]);
      setRules(r.data.rules || []);
      setPolicies(p.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    }
  }, [entityKind]);

  useEffect(() => { if (isSuntronix) load(); }, [isSuntronix, load]);

  const toggle = async (rule) => {
    setBusy((b) => ({ ...b, [rule.rule_id]: true }));
    try {
      await api.post("/preflight/rule-config", {
        rule_id: rule.rule_id,
        active: !rule.active,
        master_company_id: rule.customer_scope || null,
        notes: `Toggled via admin UI`,
      });
      toast.success(`${rule.rule_id} → ${!rule.active ? "active" : "inactive"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    } finally { setBusy((b) => ({ ...b, [rule.rule_id]: false })); }
  };

  const submitPolicy = async () => {
    if (!pForm.key.trim()) { toast.error("Key required"); return; }
    try {
      let value = pForm.value;
      if (value === "true") value = true;
      else if (value === "false") value = false;
      else if (!isNaN(Number(value)) && value.trim() !== "") value = Number(value);
      await api.post("/preflight/customer-policy", {
        key: pForm.key.trim(), value,
        master_company_id: pForm.master_company_id || null,
        source_label: pForm.source_label || null,
      });
      toast.success("Policy override saved");
      setAddPolicy(false); setPForm({ key: "", value: "true", master_company_id: "", source_label: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rules;
    return rules.filter((r) => `${r.rule_id} ${r.entity_kind} ${r.name}`.toLowerCase().includes(query));
  }, [rules, q]);

  if (!isSuntronix) {
    return (
      <div className="max-w-lg mx-auto mt-16 st-panel p-6 text-center"
            data-testid="preflight-rules-forbidden">
        <ShieldAlert className="h-8 w-8 mx-auto text-slate-400 mb-2" />
        <div className="font-heading text-lg mb-1">Suntronix internal only</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="preflight-rules-page">
      <PageHeader
        eyebrow="Administration · Rules & Policies"
        title="ONE Preflight™ — Rules"
        context="Every check that runs before a transaction advances. Deactivate rules per customer or set policy overrides that drive the effective value."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={load}
              data-testid="pfr-refresh">
              <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
            <Button variant="create" onClick={() => setAddPolicy(true)}
              data-testid="pfr-add-policy">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add policy override
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by rule id, entity or name…"
          className="max-w-sm h-9 text-[13px]"
          data-testid="pfr-search" />
        <select className="border rounded h-9 px-2 text-[12px]"
          value={entityKind} onChange={(e) => setEntityKind(e.target.value)}
          data-testid="pfr-kind-filter">
          <option value="">All entity kinds</option>
          {["deployment","redeployment","recovery","shipment","order","invoice"].map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <div className="text-[11px] text-slate-500 ml-auto">
          {filtered.length} rules · {policies.length} customer policies
        </div>
      </div>

      <Section title="Rules" testId="pfr-list">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Rule</th>
                <th className="text-left px-3 py-2">Entity</th>
                <th className="text-left px-3 py-2">Scope</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={`${r.entity_kind}-${r.rule_id}`}
                      className="border-t border-slate-100 hover:bg-slate-50"
                      data-testid={`pfr-row-${r.rule_id}`}>
                  <td className="px-3 py-2 font-mono text-[12px]">{r.rule_id}</td>
                  <td className="px-3 py-2 uppercase text-[11px] text-slate-600">{r.entity_kind}</td>
                  <td className="px-3 py-2 text-[12px] text-slate-500">
                    {r.customer_scope || "Global"}
                  </td>
                  <td className="px-3 py-2">
                    {r.active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                        <ShieldCheck className="h-3 w-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border bg-slate-100 text-slate-500 border-slate-200">
                        <ShieldAlert className="h-3 w-3" /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline"
                      onClick={() => toggle(r)}
                      disabled={busy[r.rule_id]}
                      data-testid={`pfr-toggle-${r.rule_id}`}>
                      {r.active
                        ? <><ToggleRight className="h-3.5 w-3.5 mr-1" /> Deactivate</>
                        : <><ToggleLeft className="h-3.5 w-3.5 mr-1" /> Activate</>}
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-[12px] text-slate-400">No rules match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Customer policy overrides" testId="pfr-policies">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Key</th>
                <th className="text-left px-3 py-2">Value</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr key={`${p.key}-${p.master_company_id}`}
                      className="border-t border-slate-100"
                      data-testid={`pfr-policy-${p.key}`}>
                  <td className="px-3 py-2 font-mono text-[12px]">{p.key}</td>
                  <td className="px-3 py-2 font-mono text-[12px]">{String(p.value)}</td>
                  <td className="px-3 py-2 text-[12px]">{p.master_company_id || "Global"}</td>
                  <td className="px-3 py-2 text-[12px] text-slate-500">{p.source_label || "—"}</td>
                  <td className="px-3 py-2 text-[10px] text-slate-400">{p.updated_at}</td>
                </tr>
              ))}
              {policies.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-[12px] text-slate-400">No overrides yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={addPolicy} onOpenChange={setAddPolicy}>
        <DialogContent data-testid="pfr-policy-dialog">
          <DialogHeader>
            <DialogTitle>Add customer policy override</DialogTitle>
            <div className="text-[12px] text-slate-500">
              Preflight resolves policies from most specific to least specific. Customer-scoped overrides trump the master default.
            </div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <PolField label="Key (required)">
              <select className="w-full border rounded h-9 px-2 text-[13px]"
                value={pForm.key}
                onChange={(e) => setPForm({ ...pForm, key: e.target.value })}
                data-testid="pfr-policy-key">
                <option value="">— Select a policy key —</option>
                {["mdm_required","po_required","buffer_stock_min","manual_mdm_verification_allowed",
                  "applecare_required","secondary_contact_required"].map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
            </PolField>
            <PolField label="Value">
              <Input value={pForm.value}
                onChange={(e) => setPForm({ ...pForm, value: e.target.value })}
                placeholder='true / false / 5 / "string"'
                data-testid="pfr-policy-value" />
            </PolField>
            <PolField label="Master Customer ID (optional)">
              <Input value={pForm.master_company_id}
                onChange={(e) => setPForm({ ...pForm, master_company_id: e.target.value })}
                placeholder="Leave blank for global default" />
            </PolField>
            <PolField label="Source label">
              <Input value={pForm.source_label}
                onChange={(e) => setPForm({ ...pForm, source_label: e.target.value })}
                placeholder="ABC Global — India Deployment Policy" />
            </PolField>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddPolicy(false)}>Cancel</Button>
            <Button onClick={submitPolicy} data-testid="pfr-policy-submit">
              <Plus className="h-3.5 w-3.5 mr-1" /> Save policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PolField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
